import React, { useEffect, useState } from 'react';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import bookingService from '../../../services/booking.service';
import NotesModal from './NotesModal';
import WebBookingsIcon from './subparts/WebBookingsIcon';
import { showError, showSuccess } from '../../../utils/ecxSwal';
import {
    formatBookingDate,
    formatBookingDateTime,
    getBookingStatusMeta,
    getPatientInitials,
    getPatientTypeMeta,
} from './subparts/webBookingsQuery';
import {
    getWebBookingAvatarTheme,
    WEB_BOOKING_CONTACT_THEMES,
    WEB_BOOKING_FIELD_THEMES,
} from './subparts/webBookingsTheme';

function ContactRow({ icon, value, label }) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) {
            return undefined;
        }

        const timer = window.setTimeout(() => setCopied(false), 3000);

        return () => window.clearTimeout(timer);
    }, [copied]);

    if (!value) {
        return null;
    }

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
        } catch (error) {
            console.error('Failed to copy value', error);
            showError({ title: 'Copy Failed', text: `Unable to copy ${label.toLowerCase()}.` });
        }
    };

    return (
        <div className="web-bookings-contact-row">
            <WebBookingsIcon icon={icon} theme={WEB_BOOKING_CONTACT_THEMES[icon]} size="xs" />
            <span className="text-truncate">{value}</span>
            <button
                type="button"
                className={`web-bookings-contact-row__copy${copied ? ' is-copied' : ''}`}
                title={copied ? 'Copied' : `Copy ${label}`}
                onClick={handleCopy}
                disabled={copied}
                aria-label={copied ? `${label} copied` : `Copy ${label}`}
            >
                <i className={`bi ${copied ? 'bi-check-lg' : 'bi-copy'}`}></i>
            </button>
        </div>
    );
}

function BookingField({ label, icon, theme, children, highlighted = false }) {
    return (
        <div className="web-bookings-field">
            <span className="web-bookings-field__label">
                <WebBookingsIcon icon={icon} theme={theme} size="xs" />
                {label}
            </span>
            <div
                className={`web-bookings-field__value${
                    highlighted ? ' web-bookings-field__value--notes' : ''
                }`}
            >
                {children}
            </div>
        </div>
    );
}

function BookingPatientCard({ request, index = 0, handleUpdate }) {
    const [showNotes, setShowNotes] = useState(false);
    const [updating, setUpdating] = useState(false);
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const statusMeta = getBookingStatusMeta(request.status);
    const patientTypeMeta = getPatientTypeMeta(request.isNewPatient);
    const avatarTheme = getWebBookingAvatarTheme(request.id, index);

    const updateBookingStatus = async (statusId) => {
        const updatedRequest = {
            ...request,
            status: statusId,
        };

        setUpdating(true);

        try {
            const response = await bookingService.updateBookingRequests(clinicId, updatedRequest);

            if (response?.status === 200) {
                showSuccess({
                    title: 'Status Updated',
                    text:
                        statusId === 2
                            ? 'Booking request marked as processed.'
                            : 'Booking request moved to follow up.',
                });
                handleUpdate();
            }
        } catch (error) {
            console.error('Error updating booking:', error);
            showError({ title: 'Update Failed', text: 'Failed to update booking request status.' });
        } finally {
            setUpdating(false);
        }
    };

    const hasPatientComments = request.comments && request.comments !== 'No Comments\n';

    return (
        <>
            <article className="ecx-page-list-item web-bookings-card">
                <div className="web-bookings-card__header">
                    <div className="web-bookings-card__patient">
                        <WebBookingsIcon
                            theme={avatarTheme}
                            size="sm"
                            round
                            className="web-bookings-avatar"
                        >
                            {getPatientInitials(request.firstName, request.lastName)}
                        </WebBookingsIcon>
                        <div className="min-width-0">
                            <h3 className="web-bookings-card__name">
                                {request.firstName} {request.lastName}
                                {request.preferredName && (
                                    <span className="text-muted fw-normal"> ({request.preferredName})</span>
                                )}
                                <span className={patientTypeMeta.className}>{patientTypeMeta.label}</span>
                            </h3>
                            <div className="web-bookings-card__meta">
                                <span className="web-bookings-card__meta-item">
                                    <WebBookingsIcon icon="bi-calendar-plus" theme="cyan" size="xs" />
                                    Created {formatBookingDateTime(request.createdAt)}
                                </span>
                                <span className="web-bookings-card__meta-item">
                                    <WebBookingsIcon icon="bi-pencil-square" theme="slate" size="xs" />
                                    Modified {formatBookingDateTime(request.modifiedAt)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex flex-column align-items-lg-end gap-2">
                        <span className={statusMeta.className}>
                            <i className={`bi ${statusMeta.icon}`}></i>
                            {statusMeta.label}
                        </span>
                        <div className="web-bookings-card__contact">
                            <ContactRow icon="bi-phone" value={request.cellPhone} label="Cell phone" />
                            <ContactRow icon="bi-house" value={request.homePhone} label="Home phone" />
                            <ContactRow icon="bi-envelope" value={request.email} label="Email" />
                        </div>
                    </div>
                </div>

                <div className="web-bookings-card__body">
                    <div>
                        <BookingField
                            label="Appointment Type"
                            icon="bi-calendar-event"
                            theme={WEB_BOOKING_FIELD_THEMES.appointmentType}
                        >
                            {request.bookingAppointmentType?.apptType || '—'}
                            {request.bookingAppointmentType?.timeSlot
                                ? ` (${request.bookingAppointmentType.timeSlot} minutes)`
                                : ''}
                        </BookingField>

                        <BookingField
                            label="Reason for Visit"
                            icon="bi-chat-left-text"
                            theme={WEB_BOOKING_FIELD_THEMES.reason}
                        >
                            {request.reason || '—'}
                        </BookingField>

                        {hasPatientComments && (
                            <BookingField
                                label="Patient Comments"
                                icon="bi-chat-quote"
                                theme={WEB_BOOKING_FIELD_THEMES.comments}
                            >
                                {request.comments}
                            </BookingField>
                        )}

                        {request.clinicComments && (
                            <BookingField
                                label="Clinic Notes"
                                icon="bi-journal-text"
                                theme={WEB_BOOKING_FIELD_THEMES.clinicNotes}
                                highlighted
                            >
                                {request.clinicComments}
                            </BookingField>
                        )}
                    </div>

                    <div>
                        <BookingField
                            label="Doctor"
                            icon="bi-person-badge"
                            theme={WEB_BOOKING_FIELD_THEMES.doctor}
                        >
                            {request.doctorName || '—'}
                        </BookingField>

                        <BookingField
                            label="1st Preference"
                            icon="bi-1-circle"
                            theme={WEB_BOOKING_FIELD_THEMES.firstPreference}
                        >
                            {formatBookingDate(request.firstPreferredDate)} at {request.firstPreferredTime || '—'}
                        </BookingField>

                        <BookingField
                            label="2nd Preference"
                            icon="bi-2-circle"
                            theme={WEB_BOOKING_FIELD_THEMES.secondPreference}
                        >
                            {formatBookingDate(request.secondPreferredDate)} at {request.secondPreferredTime || '—'}
                        </BookingField>
                    </div>
                </div>

                <div className="web-bookings-card__footer">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-pill web-bookings-action-btn"
                        onClick={() => setShowNotes(true)}
                        disabled={updating}
                    >
                        <i className="bi bi-pencil me-1" />
                        Add Notes
                    </button>

                    <div className="web-bookings-card__actions">
                        {(request.status === 0 || request.status === 1) && (
                            <button
                                type="button"
                                className="btn btn-sm btn-accent rounded-pill web-bookings-action-btn"
                                onClick={() => updateBookingStatus(2)}
                                disabled={updating}
                            >
                                <i className="bi bi-check-circle me-1" />
                                Mark as Processed
                            </button>
                        )}

                        {request.status === 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary rounded-pill web-bookings-action-btn"
                                onClick={() => updateBookingStatus(1)}
                                disabled={updating}
                            >
                                <i className="bi bi-clock-history me-1" />
                                Follow Up Later
                            </button>
                        )}
                    </div>
                </div>
            </article>

            <NotesModal
                show={showNotes}
                onClose={() => setShowNotes(false)}
                patientBooking={request}
                handleUpdate={handleUpdate}
            />
        </>
    );
}

export default BookingPatientCard;
